import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Heart, MapPin, GraduationCap, Users, Calendar, DollarSign, CheckCircle, AlertCircle } from 'lucide-react';
import { useGetUserChildDetailsQuery, useSponsorChildMutation, useGetProfileQuery } from '../../store/slices/apiSlice';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import toast from 'react-hot-toast';
import Navbar from '../../components/common/Navbar';

const ChildDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useSelector((state: RootState) => state.auth);
  const childId = id ? parseInt(id) : null;
  const isValidId = childId && !isNaN(childId) && childId > 0;

  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [selectedType, setSelectedType] = useState<'weekly' | 'monthly' | 'yearly' | 'one_time'>('monthly');
  const [autoRenewal, setAutoRenewal] = useState(true);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: childResponse, isLoading } = useGetUserChildDetailsQuery(childId!, {
    skip: !isValidId,
  });

  const { data: profileResponse } = useGetProfileQuery({ role: user?.role || 'USER' }, {
    skip: !user,
  });

  const [sponsorChild] = useSponsorChildMutation();

  const child = childResponse?.data;
  const profile = profileResponse?.data;
  const walletBalance = profile?.wallet_balance ? parseFloat(String(profile.wallet_balance)) : 0;
  const currencySymbol = profile?.currency_symbol || child?.currency_symbol || '$';

  const formatCurrency = (amount: number, symbol: string) => {
    return `${symbol}${amount.toLocaleString('en-US')}`;
  };

  const getSelectedAmount = () => {
    if (!child) return 0;
    return child.funding_options[selectedType] || 0;
  };

  const handleSponsor = async () => {
    if (!child) return;

    const amount = getSelectedAmount();
    
    if (walletBalance < amount) {
      toast.error('Insufficient wallet balance. Please add funds to your wallet.');
      navigate('/user/wallet');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await sponsorChild({
        child_id: child.child_id,
        sponsorship_type: selectedType,
        amount: amount,
        auto_renewal: autoRenewal,
        notes: notes || undefined,
      }).unwrap();

      if (result?.success) {
        toast.success(`Successfully sponsored ${child.name}!`);
        setShowSponsorModal(false);
        navigate('/sponsor/my-children');
      } else {
        toast.error(result?.message || 'Failed to sponsor child');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to sponsor child. Please try again.');
      console.error('Sponsor error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!child) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <Card className="p-12 text-center">
              <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Child Not Found</h2>
              <p className="text-gray-600 mb-6">The child you're looking for doesn't exist or is no longer available.</p>
              <Button onClick={() => navigate('/sponsor/children')}>Back to Children</Button>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Child Image */}
            <Card className="overflow-hidden">
              <div className="relative h-96 bg-gray-200">
                <img
                  src={child.profile_image || 'https://via.placeholder.com/800x600?text=No+Image'}
                  alt={child.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/800x600?text=No+Image';
                  }}
                />
              </div>
            </Card>

            {/* Child Information */}
            <Card className="p-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{child.name}</h1>
              
              <div className="space-y-4">
                <div className="flex items-center text-gray-600">
                  <MapPin className="w-5 h-5 mr-3" />
                  <span className="text-lg">{child.location}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <GraduationCap className="w-5 h-5 mr-3" />
                  <span className="text-lg">{child.education_stage}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Users className="w-5 h-5 mr-3" />
                  <span className="text-lg">Age: {child.age} years</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 mb-3">About {child.name}</h2>
                <p className="text-gray-700 leading-relaxed">{child.bio}</p>
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Sponsorship Options */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Sponsorship Options</h2>
              
              <div className="space-y-3 mb-6">
                {(['weekly', 'monthly', 'yearly'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setSelectedType(type)}
                    className={`w-full p-4 rounded-lg border-2 transition-all ${
                      selectedType === type
                        ? 'border-gray-800 bg-gray-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-900 capitalize">{type}</span>
                      <span className="text-lg font-bold text-gray-900">
                        {formatCurrency(child.funding_options[type], child.currency_symbol)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Wallet Balance */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Your Wallet Balance:</span>
                  <span className="text-lg font-bold text-gray-900">
                    {formatCurrency(walletBalance, currencySymbol)}
                  </span>
                </div>
                {walletBalance < getSelectedAmount() && (
                  <p className="text-xs text-red-600 mt-2">
                    Insufficient balance. Please add funds to your wallet.
                  </p>
                )}
              </div>

              {/* Sponsor Button */}
              <Button
                onClick={() => setShowSponsorModal(true)}
                className="w-full bg-gray-800 hover:bg-gray-900 flex items-center justify-center gap-2"
                disabled={walletBalance < getSelectedAmount()}
              >
                <Heart className="w-4 h-4" />
                Sponsor Now
              </Button>
            </Card>

            {/* Quick Info */}
            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Why Sponsor?</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Make a lasting impact on a child's education</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Receive regular updates on the child's progress</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Flexible sponsorship options (weekly, monthly, yearly)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Auto-renewal available for continuous support</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </div>
      </div>

      {/* Sponsor Confirmation Modal */}
      <Modal
        isOpen={showSponsorModal}
        onClose={() => {
          setShowSponsorModal(false);
          setNotes('');
        }}
        title="Confirm Sponsorship"
      >
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">You are sponsoring:</p>
            <p className="text-lg font-semibold text-gray-900">{child.name}</p>
            <p className="text-sm text-gray-600 mt-1">{child.location}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sponsorship Type
            </label>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-900 capitalize">{selectedType}</span>
                <span className="text-lg font-bold text-gray-900">
                  {formatCurrency(getSelectedAmount(), child.currency_symbol)}
                </span>
              </div>
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRenewal}
                onChange={(e) => setAutoRenewal(e.target.checked)}
                className="w-4 h-4 text-gray-800 border-gray-300 rounded focus:ring-gray-500"
                disabled={selectedType === 'one_time'}
              />
              <span className="text-sm text-gray-700">
                Enable auto-renewal {selectedType === 'one_time' && '(Not available for one-time)'}
              </span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              placeholder="Add any notes about this sponsorship..."
            />
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-sm text-gray-800">
              <strong>Amount:</strong> {formatCurrency(getSelectedAmount(), child.currency_symbol)} will be deducted from your wallet balance.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowSponsorModal(false);
                setNotes('');
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSponsor}
              disabled={isSubmitting || walletBalance < getSelectedAmount()}
              className="flex-1 bg-gray-800 hover:bg-gray-900"
            >
              {isSubmitting ? 'Processing...' : 'Confirm Sponsorship'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ChildDetails;
